import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowRight, CheckCircle2, Info, Lightbulb, MapPin, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getTradePage, tradePages } from "@/data/trade-pages";
import { cityPages } from "@/data/city-pages";
import { breadcrumbLd, faqLd, jsonLdScript, priceOffersLd, SITE_URL } from "@/lib/json-ld";

export const Route = createFileRoute("/gewerke/$gewerk")({
  staticData: { sitemap: true },
  loader: ({ params }) => {
    const trade = getTradePage(params.gewerk);
    if (!trade) throw notFound();
    return { trade };
  },
  head: ({ loaderData }) => {
    const trade = loaderData?.trade;
    if (!trade) {
      return {
        meta: [{ title: "Gewerk nicht gefunden | Begner" }, { name: "robots", content: "noindex" }],
      };
    }
    const title = `${trade.headline} | Begner`;
    const description = `${trade.name}: Richtpreise, Tipps und kostenlose Offerten von geprüften Betrieben in der Schweiz.`;
    const path = `/gewerke/${trade.slug}`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      scripts: [
        jsonLdScript({
          "@context": "https://schema.org",
          "@type": "Service",
          name: trade.name,
          serviceType: trade.name,
          description: trade.intro,
          url: `${SITE_URL}${path}`,
          areaServed: { "@type": "Country", name: "Schweiz" },
          provider: { "@type": "Organization", name: "Begner", url: SITE_URL },
          offers: priceOffersLd(trade.prices),
        }),
        jsonLdScript(faqLd(trade.faqs)),
        jsonLdScript(
          breadcrumbLd([
            { name: "Startseite", path: "/" },
            { name: "Auftragnehmer", path: "/auftragnehmer" },
            { name: trade.name, path },
          ]),
        ),
      ],
    };
  },
  notFoundComponent: () => (
    <div className="container-page py-20 text-center">
      <h1 className="font-display text-3xl font-bold">Gewerk nicht gefunden</h1>
      <Button asChild className="mt-6 rounded-xl">
        <Link to="/auftragnehmer">Alle Auftragnehmer</Link>
      </Button>
    </div>
  ),
  component: TradePageView,
});

function TradePageView() {
  const { trade } = Route.useLoaderData();
  const others = tradePages.filter((t) => t.slug !== trade.slug);

  return (
    <>
      <section className="border-b border-border bg-surface py-14 md:py-20">
        <div className="container-page max-w-4xl">
          <p className="inline-flex items-center gap-2 text-sm font-semibold text-brand-deep">
            <Wrench className="h-4 w-4" /> {trade.name}
          </p>
          <h1 className="mt-3 font-display text-4xl font-bold leading-tight md:text-5xl">
            {trade.headline}
          </h1>
          <p className="mt-5 text-lg leading-8 text-muted-foreground">{trade.intro}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg" className="rounded-xl">
              <Link to="/auftrag-einstellen">
                Offerten kostenlos anfragen <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-xl bg-card">
              <Link to="/handwerkerverzeichnis/gewerk/$gewerk" params={{ gewerk: trade.slug }}>
                Firmen im Verzeichnis ansehen
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="pt-12">
        <div className="container-page max-w-4xl">
          <div className="rounded-2xl border border-brand/30 bg-brand-soft p-6">
            <p className="inline-flex items-center gap-2 text-sm font-semibold text-brand-deep">
              <Info className="h-4 w-4" /> Kurz erklärt
            </p>
            <p className="mt-3 leading-7">
              {trade.prices[0]
                ? `${trade.prices[0].label}: In der Schweiz rechnen Sie mit ${trade.prices[0].range} (Richtwert inkl. MwSt.). `
                : ""}
              {trade.faqs[0]?.answer}
            </p>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-16">
        <div className="container-page grid max-w-4xl gap-10 md:grid-cols-2">
          <div>
            <h2 className="font-display text-2xl font-bold">Leistungen</h2>
            <ul className="mt-5 space-y-3">
              {trade.services.map((service) => (
                <li key={service} className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-brand" />
                  {service}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
            <h2 className="font-display text-2xl font-bold">Richtpreise</h2>
            <dl className="mt-5 divide-y divide-border">
              {trade.prices.map((price) => (
                <div key={price.label} className="flex justify-between gap-4 py-3 text-sm">
                  <dt className="text-muted-foreground">{price.label}</dt>
                  <dd className="shrink-0 font-semibold">{price.range}</dd>
                </div>
              ))}
            </dl>
            <p className="mt-4 text-xs text-muted-foreground">
              Unverbindliche Richtwerte inkl. MwSt. Der genaue Preis hängt von Ihrem Projekt ab.
            </p>
          </div>
        </div>
      </section>

      <section className="border-t border-border bg-surface py-14">
        <div className="container-page max-w-4xl">
          <h2 className="font-display text-2xl font-bold">Tipps für Ihre Anfrage</h2>
          <ul className="mt-5 grid gap-4 md:grid-cols-3">
            {trade.tips.map((tip) => (
              <li
                key={tip}
                className="rounded-2xl border border-border bg-card p-5 text-sm leading-6"
              >
                <Lightbulb className="mb-3 h-5 w-5 text-brand" />
                {tip}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="py-14">
        <div className="container-page max-w-4xl">
          <h2 className="font-display text-2xl font-bold">Häufige Fragen</h2>
          <div className="mt-5 space-y-3">
            {trade.faqs.map((faq) => (
              <details
                key={faq.question}
                className="group rounded-2xl border border-border bg-card p-5"
              >
                <summary className="cursor-pointer font-semibold">{faq.question}</summary>
                <p className="mt-3 text-sm leading-6 text-muted-foreground">{faq.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-border bg-surface py-12">
        <div className="container-page max-w-4xl space-y-8">
          <div>
            <h2 className="font-display text-xl font-bold">{trade.name} in Ihrer Region</h2>
            <div className="mt-5 flex flex-wrap gap-3">
              {cityPages.map((city) => (
                <Link
                  key={city.slug}
                  to="/handwerker/$stadt/$gewerk"
                  params={{ stadt: city.slug, gewerk: trade.slug }}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-4 py-2 text-sm font-medium hover:border-brand hover:text-brand-deep"
                >
                  <MapPin className="h-4 w-4 text-brand" /> {trade.name} {city.name}
                </Link>
              ))}
            </div>
          </div>
          <div>
            <h2 className="font-display text-xl font-bold">Weitere Gewerke</h2>
            <div className="mt-5 flex flex-wrap gap-3">
              {others.map((t) => (
                <Link
                  key={t.slug}
                  to="/gewerke/$gewerk"
                  params={{ gewerk: t.slug }}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-4 py-2 text-sm font-medium hover:border-brand hover:text-brand-deep"
                >
                  <Wrench className="h-4 w-4 text-brand" /> {t.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
